import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  type ObjectSearchRequest,
  type ObjectSearchSuggestion,
  type ValueEditorDescriptor,
  type ValueEditorDropdownKind,
} from '@bizzdesign/data-access-search';
import {
  AttributeEditorComponent,
  ObjectSearchPanelComponent,
} from '@bizzdesign/feature-content-details';
import { Combined } from '@bizzdesign/sdk-knowledgegraph';
import type {
  AttributeCategoryDefinitionDto,
  AttributeDataPayload,
  BDTreeNode,
  DisplayText,
  DropdownSettings,
} from '@bizzdesign/shared/models';
import { TranslateDisplayTextPipe } from '@bizzdesign/shared/pipes';
import { DropdownComponent } from '@bizzdesign/ui-dropdown';
import { AutoFocusModule } from 'primeng/autofocus';
import { InputTextModule } from 'primeng/inputtext';
import { type Popover, PopoverModule } from 'primeng/popover';

/** The text rendered on the value trigger, optionally prefixed by an icon. */
export type ValueEditorLabel = { icon?: string } & DisplayText<'text'>;

const DROPDOWN_DATA_ID_SUFFIX: Record<ValueEditorDropdownKind, string> = {
  enum: 'enum-dropdown',
  boolean: 'boolean-dropdown',
  scope: 'scope-dropdown',
  elementType: 'element-type-dropdown',
  relationObjectType: 'relation-object-type-dropdown',
  relationTarget: 'relation-target-dropdown',
  user: 'user-dropdown',
};

const PANEL_STYLE_CLASS = 'bd-popover-rounded bd-value-editor-popover';
const ATTRIBUTE_PANEL_STYLE_CLASS = `${PANEL_STYLE_CLASS} bd-value-editor-attribute-panel`;

/**
 * Renders the pill-styled inline control described by a {@link ValueEditorDescriptor}
 * and emits the raw selection its consumer needs to map back onto its own model.
 *
 * @remarks Presentational only, and deliberately ignorant of what the value belongs
 * to — it owns the control, its popover and its edit state, while the consumer owns
 * the descriptor and what a selection means. Consumed by
 * {@link InteractiveFilterValueComponent} for applied filters and by the AI overview
 * for the parameters of a natural-language query.
 *
 * The two object-backed values — a reference attribute and a relation on a specific
 * object — open the Repository search panel rather than a bare field, so the search and
 * its results share one card instead of stacking two overlays.
 */
@Component({
  selector: 'bd-value-editor',
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    DropdownComponent,
    AttributeEditorComponent,
    ObjectSearchPanelComponent,
    AutoFocusModule,
    InputTextModule,
    PopoverModule,
    TranslateDisplayTextPipe,
  ],
  templateUrl: './value-editor.component.html',
  styleUrl: './value-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.bd-value-editor--active]': 'editingValue()',
  },
})
export class ValueEditorComponent {
  /**
   * This component's own host — the visible chip — which every popover here positions
   * against rather than against the trigger it wraps, since that trigger is inset by
   * this host's own padding.
   */
  readonly hostElement = inject(ElementRef<HTMLElement>).nativeElement;

  private readonly injector = inject(Injector);

  /** Chrome and placement shared by every popover the chip opens but the dropdown's. */
  readonly panelStyleClass = PANEL_STYLE_CLASS;

  descriptor = input.required<ValueEditorDescriptor>();
  /** The label rendered on the trigger; option-list values label themselves. */
  valueLabel = input<ValueEditorLabel>();
  attributeCategoryDefinitions = input<AttributeCategoryDefinitionDto[]>([]);
  disabled = input<boolean>(false);
  /** Prefixes every `data-id` so each consumer keeps its own test hooks. */
  dataIdPrefix = input<string>('value-editor');
  /** Accessible name for the `text` editor's field, describing what it edits. */
  textInputLabel = input<string>();

  /** Whether the dropdown is still loading the options it filters server-side. */
  optionsLoading = input<boolean>(false);
  /** Results of the last {@link searchObjects}, listed by both Repository search panels. */
  objectSuggestions = input<ObjectSearchSuggestion[]>([]);
  objectSuggestionsLoading = input<boolean>(false);

  selectNode = output<BDTreeNode | undefined>();
  attributeChange = output<AttributeDataPayload>();
  objectChange = output<ObjectSearchSuggestion>();
  textChange = output<string>();
  filterOptions = output<string>();
  searchObjects = output<ObjectSearchRequest>();

  /**
   * Whether a free value is being edited. Those value types show a link-styled
   * trigger until clicked, then swap to their entry control (option-list value
   * types open their list directly instead).
   */
  readonly editingValue = signal(false);

  /**
   * Settings for the resolved dropdown. Object-type dropdowns show the selected type's
   * icon inside the control, so the icon reads as part of the value rather than as a
   * separate marker on the chip.
   */
  readonly dropdownSettings = computed<DropdownSettings>(() => {
    const descriptor = this.descriptor();
    if (descriptor.kind !== 'dropdown') {
      return this.valueDropdownSettings;
    }
    if (descriptor.dropdownKind === 'user') {
      return this.filterableDropdownSettings;
    }
    const icon = descriptor.selected?.icon;
    return icon
      ? { ...this.valueDropdownSettings, icon }
      : this.valueDropdownSettings;
  });

  readonly valueDropdownSettings: DropdownSettings = {
    inputType: 'unstyled',
    layout: 'list',
    selectionMode: 'single',
    isSmallInput: true,
    tScope: 'featureSearch',
    dropdownPanelClass:
      'bd-value-editor-popover bd-value-editor-dropdown-panel',
  };

  readonly filterableDropdownSettings: DropdownSettings = {
    inputType: 'unstyled',
    layout: 'list',
    selectionMode: 'single',
    isSmallInput: true,
    tScope: 'featureSearch',
    dropdownWidth: '300px',
    filterOptionsCount: 0,
    skipDefaultFilter: true,
    filterPlaceholderKey: 'peopleFilter.userSearchPlaceholder',
    dropdownPanelClass:
      'bd-value-editor-popover bd-value-editor-dropdown-panel',
  };

  /** The attribute being edited when it holds a reference, which the panel searches for. */
  private readonly referencePayload = computed<
    AttributeDataPayload | undefined
  >(() => {
    const descriptor = this.descriptor();
    return descriptor.kind === 'attribute' &&
      descriptor.payload?.type === 'reference'
      ? descriptor.payload
      : undefined;
  });

  /** Plural type label the reference panel reads out, e.g. `organizations`. */
  readonly referenceTypeLabel = computed<string>(() => {
    const referenceType = this.referencePayload()?.referenceType;
    const definition = Object.values(Combined.elementDefinitions).find(
      (elementDefinition) => elementDefinition.key === referenceType,
    );
    return definition?.pluralLabel.toLowerCase() ?? '';
  });

  /** A reference brings the search panel's own card, so only a bare input gets the padding. */
  readonly attributePopoverStyleClass = computed<string>(() =>
    this.referencePayload() ? PANEL_STYLE_CLASS : ATTRIBUTE_PANEL_STYLE_CLASS,
  );

  /** Falls back to the current value so the field is never left without a name. */
  readonly textInputAriaLabel = computed<string | undefined>(() => {
    const descriptor = this.descriptor();
    const fallback = descriptor.kind === 'text' ? descriptor.value : '';
    return this.textInputLabel() || fallback || undefined;
  });

  dropdownDataId(dropdownKind: ValueEditorDropdownKind): string {
    return `${this.dataIdPrefix()}-${DROPDOWN_DATA_ID_SUFFIX[dropdownKind]}`;
  }

  // PrimeNG aligns before `onShow`, so it measures the panel before this renders its content.
  startEditing(popover: Popover): void {
    if (this.disabled()) {
      return;
    }
    this.editingValue.set(true);
    afterNextRender(() => popover.align(), { injector: this.injector });
  }

  stopEditing(): void {
    this.editingValue.set(false);
  }

  /** Commit a free-value edit on Enter by blurring the field, reusing the commit-on-blur path. */
  commitValueEdit(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLElement) {
      target.blur();
    }
  }

  onTextChange(value: string, previousValue: string): void {
    this.stopEditing();
    if (value === previousValue) {
      return;
    }
    this.textChange.emit(value);
  }

  onAttributeChange(payload: AttributeDataPayload): void {
    this.stopEditing();
    this.attributeChange.emit(payload);
  }

  onObjectChange(object: ObjectSearchSuggestion): void {
    this.stopEditing();
    this.objectChange.emit(object);
  }

  onObjectSearch(searchTerm: string): void {
    this.searchObjects.emit({ searchTerm, referenceType: undefined });
  }

  onReferenceSearch(searchTerm: string): void {
    this.searchObjects.emit({
      searchTerm,
      referenceType: this.referencePayload()?.referenceType,
    });
  }

  clearObjectSearch(): void {
    this.searchObjects.emit({ searchTerm: '', referenceType: undefined });
  }

  clearReferenceObjectSearch(): void {
    if (this.referencePayload()) {
      this.clearObjectSearch();
    }
  }

  onReferenceObjectChange(
    payload: AttributeDataPayload,
    object: ObjectSearchSuggestion,
  ): void {
    this.stopEditing();
    this.attributeChange.emit({
      ...payload,
      type: 'reference',
      value: object.id,
      objectName: object.name,
      description: object.description,
      systemAttributes: object.systemAttributes,
      externalSource: object.externalSource,
    });
  }
}
